import { type JSX, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { trackCartCheckoutStep } from '@jsmdg/tracking';
import { StepBar, type StepBarStep, useIntersection } from '@jsmdg/yoshi';
import { type HeaderContext } from '../../types';
import { CheckoutSteps } from '../CheckoutSteps/CheckoutSteps';
import { CheckoutStepsWrapper } from '../CheckoutSteps/CheckoutStepsWrapper';
import styles from './StepsBar.module.scss';

type StepsBarProps = {
    readonly step: number;
};

const messages = defineMessages({
    shoppingCart: {
        defaultMessage: 'Warenkorb',
    },
    a11yShoppingCartLabel: {
        defaultMessage: 'Schritt 1, Warenkorb bearbeiten',
    },
    orderData: {
        defaultMessage: 'Bestelldaten',
    },
    a11yOrderDataLabel: {
        defaultMessage: 'Schritt 2, Bestelldaten bearbeiten',
    },
    lastCheck: {
        defaultMessage: 'Letzter Check',
    },
});

const StepsBar = ({ step }: StepsBarProps): JSX.Element | null => {
    const intl = useIntl();
    const { cartEmpty, cartUrl, checkoutUrl, featureFlagsVariation } =
        useFragmentContext<HeaderContext>();

    const isCheckoutImprovementEnabled = featureFlagsVariation?.checkout_improvement === 'on';

    const onStepClick = (stepBarStep: StepBarStep, stepIndex: number): void => {
        if (stepBarStep.url) {
            trackCartCheckoutStep(
                stepIndex,
                `${window.location.origin}${stepBarStep.url}`,
                stepBarStep.title,
            );

            window.location.href = stepBarStep.url;
        }
    };

    const steps: StepBarStep[] = [
        {
            title: intl.formatMessage(messages.shoppingCart),
            url: cartUrl,
            onClick: onStepClick,
            a11yLabel: intl.formatMessage(messages.a11yShoppingCartLabel),
        },
        {
            title: intl.formatMessage(messages.orderData),
            url: checkoutUrl,
            onClick: onStepClick,
            a11yLabel: intl.formatMessage(messages.a11yOrderDataLabel),
        },
        { title: intl.formatMessage(messages.lastCheck), a11yLabel: '' },
    ];

    const wasSticky = useRef(false);
    const [isIntersecting, setTargetElement] = useIntersection({ threshold: 1 });

    useEffect(() => {
        // Set wasSticky to true after the page is loaded
        wasSticky.current = true;
    }, []);

    const isSticky = !isIntersecting && wasSticky.current;

    if (step < 1) {
        return null;
    }

    if (isCheckoutImprovementEnabled) {
        return cartEmpty ? null : (
            <div
                data-id="stepbar-wrapper"
                data-testid="stepbar-wrapper"
                ref={node => node && setTargetElement(node)}
                className={classNames('w-100', 'py-md-1x')}
            >
                <div
                    className={classNames(
                        'w-100 d-flex justify-content-between',
                        styles.checkoutSteps,
                        { [styles.fixedTop]: isSticky },
                        { 'd-none d-md-flex': step === 1 },
                    )}
                >
                    <StepBar activeIndex={step - 1} steps={steps} ariaLabel="" />
                </div>
            </div>
        );
    }

    return (
        <div className="d-flex justify-content-center justify-content-md-start">
            <CheckoutStepsWrapper>
                <CheckoutSteps step={step} />
            </CheckoutStepsWrapper>
        </div>
    );
};

export { StepsBar };
